import { isEmpty, isPlainObject } from 'lodash-es';
import { EdmundsAPI, EdmundsMultiOfferAPI } from 'client/data/api/api-client';
import { CANVAS_CREATIVE_ID, PREDEFINED_MESSAGE, ROLE } from 'site-modules/canvas/constants/canvas';
import { setCookie } from 'client/utils/cookie/helpers/set-cookie';
import { TIME } from 'client/utils/time';
import { getCookie } from 'client/utils/cookie/helpers/get-cookie';
import { getSessionHeaders } from 'site-modules/canvas/utils/headers';
import { createPageContextGetter } from 'site-modules/canvas/utils/get-page-context';
import { getCurrentUser } from 'site-modules/shared/components/profile/firebase-auth';
import { getProfile } from 'site-modules/shared/components/profile/idm/idm';
import { logger } from 'client/utils/logger/logger';
import { getMetricApiOptions } from 'client/utils/metrics-hoc';

const HISTORY_COOKIE = 'chat-history';

const LLM_TIMEOUT = 10000;

const PREDEFINED_MESSAGES = Object.values(PREDEFINED_MESSAGE);

const IDM_DATA_KEYS = [
  'appraisalMultiOffersHistory',
  'appraisalOffersHistory',
  'financing',
  'leads',
  'mergedAttributes',
  'person',
  'savedSearches',
  'vehicles',
];

const BASE_EXTRA_MAP = {
  [PREDEFINED_MESSAGE.SUGGESTION]: 'canvas_chat_follow_up',
  DEFAULT: 'canvas_chat',
};

const VENOM_X_PRODUCT_ID_HEADER = { 'X-Product-Id': 'venom' };

export const APP_MAP = {
  [PREDEFINED_MESSAGE.SUGGESTION]: 'canvas_chat_follow_up_app',
  DEFAULT: 'canvas_chat_app',
};

const SEARCH_658_MCP_APP_MAP = {
  chal: {
    ...APP_MAP,
    DEFAULT: 'chat_canvas_app_v3',
  },
};

export const getAppKeyMap = ({ search658Recipe }) => SEARCH_658_MCP_APP_MAP[search658Recipe] || APP_MAP;

const removeIdmReferences = value => {
  if (Array.isArray(value)) {
    return value.map(removeIdmReferences);
  }

  if (!isPlainObject(value)) {
    return value;
  }

  return Object.entries(value).reduce((result, [key, nestedValue]) => {
    if (key === '$ref' || key.endsWith('Ref')) {
      return result;
    }

    return { ...result, [key]: removeIdmReferences(nestedValue) };
  }, {});
};

const getAvailableIdmData = profileIdmData => {
  if (!isPlainObject(profileIdmData) || profileIdmData.$partial) {
    return {};
  }

  const availableIdmData = IDM_DATA_KEYS.reduce((result, key) => {
    if (Object.prototype.hasOwnProperty.call(profileIdmData, key)) {
      return { ...result, [key]: profileIdmData[key] };
    }
    return result;
  }, {});

  return removeIdmReferences(availableIdmData);
};

const getFreshProfileData = async () => {
  try {
    return await getProfile();
  } catch (error) {
    logger.warn(error, '[CanvasLlmClient] fresh IDM profile fetch failed, continuing without IDM context');
    return null;
  }
};

const getFreshModsData = async (profile, apiMetrics) => {
  const modsId = profile?.appraisalMultiOffers?.modsId;
  if (typeof modsId !== 'string' || !modsId.trim()) {
    return {};
  }

  try {
    const record = await EdmundsMultiOfferAPI.fetchJson(`/v1/offer-events/${modsId.trim()}`, {
      headers: {
        ...getMetricApiOptions(apiMetrics).headers,
        ...VENOM_X_PRODUCT_ID_HEADER,
      },
    });
    if (isEmpty(record)) {
      return {};
    }
    return record;
  } catch (error) {
    logger.warn(error, '[CanvasLlmClient] fresh MODS fetch failed, continuing without MODS context');
    return {};
  }
};

export class CanvasLlmClient {
  constructor({
    visitorId,
    sessionId,
    visitorLocation,
    sessionEntryParams,
    iframeRef,
    search658Recipe = 'ctrl',
    createOrUpdateIdmData,
    apiMetrics,
    appMap = null,
    extraMap = BASE_EXTRA_MAP,
  }) {
    this.abortControllers = {};
    this.history = null;
    this.authContextPromise = null;
    this.createOrUpdateIdmData = createOrUpdateIdmData;
    this.apiMetrics = apiMetrics;
    this.userDetails = {
      zipCode: visitorLocation?.zipCode,
      city: visitorLocation?.city,
      state: visitorLocation?.state,
      salesTax: visitorLocation?.salesTax,
      entryPage: sessionEntryParams?.entryPage,
      entryUrl: sessionEntryParams?.entryUrl,
      visitorId,
    };
    this.sessionId = sessionId;
    this.visitorId = visitorId;
    this.iframeRef = iframeRef;
    this.search658Recipe = search658Recipe;
    this.appMap = appMap || getAppKeyMap({ search658Recipe });
    this.extraMap = extraMap;
    this.getPageContext = createPageContextGetter(iframeRef);
  }

  updateAuthContext({ createOrUpdateIdmData, apiMetrics } = {}) {
    this.createOrUpdateIdmData = createOrUpdateIdmData;
    this.apiMetrics = apiMetrics;
  }

  updateVisitorLocation(visitorLocation) {
    this.userDetails = {
      ...this.userDetails,
      zipCode: visitorLocation?.zipCode,
      city: visitorLocation?.city,
      state: visitorLocation?.state,
      salesTax: visitorLocation?.salesTax,
    };
  }

  async getUserContext() {
    if (this.authContextPromise) {
      return this.authContextPromise;
    }

    this.authContextPromise = (async () => {
      try {
        let user = await getCurrentUser();

        if (!user && this.createOrUpdateIdmData) {
          await this.createOrUpdateIdmData({
            idmDataToSave: {},
            signInAnonymouslyTracking: { creativeId: CANVAS_CREATIVE_ID },
          });
          user = await getCurrentUser();
        }

        const profile = await getFreshProfileData();

        return {
          existingProfile: user?.uid ? { id: `firebase:${user.uid}` } : {},
          idm: getAvailableIdmData(profile),
          mods: await getFreshModsData(profile, this.apiMetrics),
        };
      } catch (error) {
        logger.warn(error, '[CanvasLlmClient] getUserContext failed, continuing without auth context');
        return { existingProfile: {}, idm: {}, mods: {} };
      } finally {
        this.authContextPromise = null;
      }
    })();

    return this.authContextPromise;
  }

  async askLlm(message, { respId } = {}) {
    const appName = this.appMap[message] || this.appMap.DEFAULT;
    const shouldStoreHistory = message !== PREDEFINED_MESSAGE.SUGGESTION;
    const isPredefinedMessage = PREDEFINED_MESSAGES.includes(message);
    this.abort(appName);
    this.abortControllers[appName] = new AbortController();
    const abortSignal = this.abortControllers[appName].signal;
    const userContext = isPredefinedMessage ? null : await this.getUserContext();

    const commonExtra = {
      user_details: this.userDetails,
      current_page_context: this.getPageContext(),
    };
    const requestExtra = isPredefinedMessage
      ? commonExtra
      : {
          existing_profile: userContext.existingProfile,
          idm: userContext.idm,
          mods: userContext.mods,
          ...commonExtra,
        };
    const response = await EdmundsAPI.fetchJson(
      `/llm/v1/ask/${appName}/dst-db-ext-source-fit-responses/?meta=true&cache=false&unsafe_ok=false&store_history=${shouldStoreHistory}${respId ? `&resp_uuid=${respId}` : ''}`,
      {
        method: 'POST',
        headers: {
          ...getMetricApiOptions(this.apiMetrics).headers,
          'Content-Type': 'application/json',
          ...getSessionHeaders({ visitorId: this.visitorId, sessionId: this.sessionId }),
        },
        body: JSON.stringify({
          question: message,
          extra: {
            [this.extraMap[message] || this.extraMap.DEFAULT]: requestExtra,
          },
          ...(this.history?.uuid ? { history: this.history } : {}),
        }),
        timeout: LLM_TIMEOUT,
        signal: abortSignal,
      }
    );

    if (shouldStoreHistory) {
      this.history = response?.thread;
      document.cookie = setCookie(HISTORY_COOKIE, this.history, {
        path: '/',
        maxAge: TIME.SEC.ONE_DAY * 28,
        sameSite: true,
      });
    }

    return response?.id;
  }

  fetchMessageHistory() {
    const historyCookie = getCookie(HISTORY_COOKIE);
    this.history = historyCookie?.uuid ? historyCookie : { uuid: historyCookie };
    if (!this.history?.uuid) {
      return Promise.resolve([]);
    }

    this.abortControllers.history = new AbortController();
    return EdmundsAPI.fetchJson(`/llm/v1/history/${this.history.uuid}/`, {
      headers: getMetricApiOptions(this.apiMetrics).headers,
      signal: this.abortControllers.history.signal,
    })
      .then(historyMessages =>
        historyMessages
          .map(({ role, content, struct, response_id: historyResponseId }, index) => ({
            id: `${index}`,
            role,
            text: content,
            struct,
            historyResponseId,
            userText:
              role === ROLE.USER
                ? undefined
                : historyMessages
                    .slice(0, index)
                    .reverse()
                    .find(msg => msg.role === ROLE.USER)?.content, // Find the closest previous user message for assistant messages
          }))
          .filter(({ text }) => text !== PREDEFINED_MESSAGE.WELCOME)
      )
      .catch(() => {
        this.clearHistory();
        return [];
      });
  }

  clearHistory() {
    document.cookie = setCookie(HISTORY_COOKIE, '', { path: '/', maxAge: 0, sameSite: true });
    this.history = null;
  }

  abort(appName) {
    if (appName) {
      this.abortControllers?.[appName]?.abort?.();
      return;
    }

    Object.values(this.abortControllers).forEach(abortController => {
      abortController?.abort?.();
    });
  }
}
